import { useEffect, useMemo, useState } from "react";
import useAccountPartCategories from "../../data/useAccountPartCategories";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccount } from "../../models/account/IAccount";
import { IPartCategory } from "../../models/Part";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PartCategoryCombo from "../leads/LeadParts/PartCategoryCombo";
import LoadingWheel from "../shared/LoadingWheel";

interface AccountPartCategoriesProps {
  account: IAccount;
}

export default function AccountPartCategories({
  account,
}: AccountPartCategoriesProps) {
  const { accountPartCategories, update } = useAccountPartCategories(account.id, "AccountPartCategories");
  const { saveHandlers } = useDefaultCRUDHandlers("Account Part Categories");
  const [selectedCategories, setSelectedCategories] = useState<IPartCategory[]>([]);

  const linkedCategoryIds = useMemo(
    () => (accountPartCategories.data?.data ?? []).map((category) => category.id),
    [accountPartCategories.data]
  );

  const sortedSelectedCategories = useMemo(
    () =>
      selectedCategories
        .slice()
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [selectedCategories]
  );

  useEffect(() => {
    setSelectedCategories(accountPartCategories.data?.data ?? []);
  }, [accountPartCategories.data]);

  const isLoading = accountPartCategories.isLoading;
  const isError = accountPartCategories.isError;

  const handleAddCategory = (category: IPartCategory) => {
    if (!category?.id) return;

    setSelectedCategories((prev) => {
      if (prev.some((selectedCategory) => selectedCategory.id === category.id)) {
        return prev;
      }

      return [...prev, category];
    });
  };

  const handleRemoveCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.filter((category) => category.id !== categoryId)
    );
  };

  const handleSave = () => {
    update.mutate(
      { partCategoryIds: selectedCategories.map((category) => category.id) },
      saveHandlers
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-red-600 font-medium">
        Unable to load part categories.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Part Categories</h3>
        <ButtonInput
          label="Save"
          isSubmit={false}
          onClick={handleSave}
          classes=""
          isLoading={update.isLoading}
        />
      </div>

      <p className="text-sm text-gray-500">
        Choose which part categories this account can use.
      </p>

      <PartCategoryCombo
        handleAddCategory={handleAddCategory}
        placeholder="Search and add a part category"
      />

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">
          Selected Categories ({sortedSelectedCategories.length})
        </p>

        {sortedSelectedCategories.length === 0 ? (
          <div className="text-sm text-gray-600 border border-gray-200 rounded-md bg-white p-3">
            No categories selected.
          </div>
        ) : (
          <div className="space-y-2">
            {sortedSelectedCategories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <span className="text-sm text-gray-800">{category.name}</span>
                <ButtonInput
                  label="Remove"
                  isSubmit={false}
                  onClick={() => handleRemoveCategory(category.id)}
                  classes="!bg-red-500 hover:!bg-red-600"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
